/* eslint-disable indent */
import { Address } from '@src/main/modules/messages/domain/address/address.interface';
import { createRequestFromAddress } from '@src/main/modules/messages/infrastructure/dto/createAddressRequest';
import { UpdateAddressRequest } from '@src/main/modules/messages/infrastructure/dto/updateAddressRequest';
import AddressForm from '@src/main/modules/messages/ui/addresses/AddressForm';
import { getAddressSchema } from '@src/main/modules/messages/ui/addresses/addressSchema';
import { useAddresses } from '@src/main/modules/messages/ui/addresses/hooks/useAddresses';
import { useCreateAddress } from '@src/main/modules/messages/ui/addresses/hooks/useCreateAddress';
import { useDeleteAddress } from '@src/main/modules/messages/ui/addresses/hooks/useDeleteAddress';
import { useUpdateAddress } from '@src/main/modules/messages/ui/addresses/hooks/useUpdateAddress';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeAddress } from '@src/main/modules/messages/utils/formatters';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface TableRow extends Address {
  id: string | number;
}

const Addresses: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: addressesTable = [], isLoading: loadingAddresses, repository } = useAddresses();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditAddress, setNewEditAddress] = useState<Address>({
    addressId: '',
    email: '',
    description: '',
  });
  const hasEditPermissions = hasPermission(PERMISSIONS.MESSAGES.ADDRESSES.edit);
  const [selectedAddresses, setSelectedAddresses] = useState<Address[]>([]);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [false, hasEditPermissions, selectedAddresses.length > 0, true, false, false];

  const addressesWithId: TableRow[] = addressesTable.map((a) => ({
    id: a.addressId!,
    ...a,
  }));

  const FILE_NAME = t('messages.addresses.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    addressesTable,
    normalizeAddress,
    FILE_NAME,
    t,
  );

  const { mutateAsync: createAddress } = useCreateAddress();
  const { mutateAsync: updateAddress } = useUpdateAddress();
  const { mutateAsync: deleteAddress } = useDeleteAddress();

  const handleShowCreateAddress = () => {
    setNewEditAddress({
      addressId: '',
      email: '',
      description: '',
    });
    setIsModalVisible(true);
  };

  const handleEditAddress = (row: TableRow) => {
    const address: Address = {
      addressId: row.addressId,
      email: row.email,
      description: row.description,
    };
    setNewEditAddress(address);
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!repository) return;
    const schema = getAddressSchema(t);

    try {
      schema.parse(newEditAddress);

      if (newEditAddress.addressId) {
        const updateRequest: UpdateAddressRequest = {
          ...newEditAddress,
          addressId: newEditAddress.addressId,
        };
        await updateAddress(updateRequest);
        showSuccessMessage(toast, t('messages.addresses.updatedSuccess'));
      } else {
        const createRequest = createRequestFromAddress(newEditAddress);
        await createAddress(createRequest);
        showSuccessMessage(toast, t('messages.addresses.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['address'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0];
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleDeleteAddress = (row: TableRow) => {
    const address: Address = {
      addressId: row.addressId,
      email: row.email,
      description: row.description,
    };
    setAddressToDelete(address);
  };

  const confirmDelete = async () => {
    if (!repository) return;

    try {
      if (isBulkDelete) {
        await Promise.all(selectedAddresses.map((addr) => deleteAddress(addr.addressId!)));
        setSelectedAddresses([]);
      } else if (addressToDelete) {
        await deleteAddress(addressToDelete.addressId!);
        setSelectedAddresses((prev) =>
          prev.filter((a) => a.addressId !== addressToDelete.addressId),
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['address'] });
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setAddressToDelete(null);
      setIsBulkDelete(false);
      showSuccessMessage(toast, t('messages.addresses.deletedSuccess'));
    }
  };

  const rejectDelete = () => {
    setAddressToDelete(null);
    setIsBulkDelete(false);
  };

  const handleSelectedChange = (rows: TableRow[]) => {
    const addresses: Address[] = rows.map((r) => ({
      addressId: r.addressId,
      email: r.email,
      description: r.description,
    }));
    setSelectedAddresses(addresses);
  };

  const deleteSelectedAddresses = () => {
    if (selectedAddresses.length === 0 || !repository) return;
    setIsBulkDelete(true);
  };

  const handleChange = (field: keyof typeof newEditAddress, value: string) => {
    setNewEditAddress((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <ContentCard>
      <HeaderTitle title={t('messages.addresses.title')} />
      <TableHeader
        onCreate={handleShowCreateAddress}
        onDelete={deleteSelectedAddresses}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['address'] });
          setIsRefreshing(false);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbar}
        exportable
      />

      <AppLoading isLoading={loadingAddresses || isRefreshing}>
        <AppTable
          headers={[
            { name: t('messages.addresses.description'), field: 'description' },
            { name: t('messages.addresses.email'), field: 'email' },
          ]}
          data={addressesWithId}
          onEdit={hasEditPermissions ? handleEditAddress : undefined}
          onDelete={hasEditPermissions ? handleDeleteAddress : undefined}
          onSelectedChange={hasEditPermissions ? handleSelectedChange : undefined}
        />

        <AppDialog
          visible={isModalVisible}
          title={
            newEditAddress.addressId
              ? t('messages.addresses.updateAddress')
              : t('messages.addresses.createAddress')
          }
          onClose={() => setIsModalVisible(false)}
          disableButtons={loadingAddresses}
          onConfirm={handleCreateEdit}
        >
          <AddressForm
            values={newEditAddress}
            handleChange={handleChange}
            errors={validationErrors}
          />
        </AppDialog>
        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || addressToDelete !== null}
          message={
            isBulkDelete
              ? t('messages.addresses.confirmBulkDelete', {
                  emails: formatList(
                    selectedAddresses.map((a) => a.email),
                    t('common.and'),
                  ),
                })
              : t('messages.addresses.confirmDelete', {
                  email: addressToDelete?.email,
                })
          }
        />
      </AppLoading>
    </ContentCard>
  );
};

export default Addresses;
